import React, { useEffect, useState } from 'react'
import Pagination from '../Customers/Pagination'
import { useDispatch, useSelector } from 'react-redux'
import {
	selectCloudReport,
	selectCloudReportFilters,
	selectCloudTiers,
} from '../Reports/reports.selectors'
import {
	exportCloudReport,
	fetchCloudReport,
	getCloudTiers,
} from '../Reports/reports.asynActions'
import { Button } from '@/components/ui/button'
import { FilterOperators, FilterTypes } from '../Reports/reports.constants'
import BusyIndicator, { REGIONS } from '../../widgets/busyIndicator'
import { setCloudReportFilters } from '../Reports/reports.slice'
import moment from 'moment'
import Popup from 'reactjs-popup'
import CloudTierDropdownHeaderFilter from './SalesReportHeaderHelper/CloudTierDropdownHeaderFilter'
import DynamicOperatorsHeaderFilter from './SalesReportHeaderHelper/DynamicOperatorsHeaderFilter'
import DateRangeHeaderFilter from './SalesReportHeaderHelper/DateRangeHeaderFilter'
import BooleanHeaderFilter from './SalesReportHeaderHelper/BooleanHeaderFilter'
import StringHeaderFilter from './SalesReportHeaderHelper/StringHeaderFilter'
import CheckBoxHeaderFilter from './SalesReportHeaderHelper/CheckBoxHeaderFilter'
import PageLayout from '../../components/ui/page-layout'
import { DataTable } from '@/components/DataTable'

const SalesReport = () => {
	const dispatch = useDispatch()
	const reportsTableResult = useSelector(selectCloudReport)
	const reportFilters = useSelector(selectCloudReportFilters)
	const cloudTiers = useSelector(selectCloudTiers)
	const [isExporting, setIsExporting] = useState(false)

	function DateFormatter(date) {
		return date != null ? moment(date).format('MM/DD/YYYY') : null
	}

	function CurrencyFormatter(amount) {
		return amount != null ? `$${amount.toFixed(2)}` : ''
	}

	useEffect(() => {
		HandleFetchSalesReport()
		dispatch(getCloudTiers())
	}, [dispatch])

	function HeaderFormatter(column, colIndex, components, specificFilters) {
		if (
			column.FilterType === 'Operation' &&
			reportFilters[`${column.dataField}`]?.filterOperationType == null
		)
			dispatch(
				setCloudReportFilters({
					...reportFilters,
					[`${column.dataField}`]: {
						...reportFilters[`${column.dataField}`],
						filterOperationType: FilterOperators.Equal,
					},
				})
			)

		function RenderFilterOperations() {
			switch (column.FilterType) {
				case FilterTypes.Numeric:
					return <CloudTierDropdownHeaderFilter />
				case FilterTypes.DynamicOperation:
					return <DynamicOperatorsHeaderFilter column={column} />
				case FilterTypes.DateRange:
					return <DateRangeHeaderFilter column={column} />
				case FilterTypes.Boolean:
					return <BooleanHeaderFilter column={column} />
				case FilterTypes.CheckBox:
					return (
						<CheckBoxHeaderFilter
							column={column}
							specificFilters={specificFilters}
						/>
					)
				case FilterTypes.String:
				default:
					return <StringHeaderFilter column={column} />
			}
		}

		return (
			<>
				<Popup
					trigger={<div>{column.text}</div>}
					closeOnDocumentClick
					keepTooltipInside={'.table'}
				>
					<div
						className={'container-fluid d-flex headerFilter'}
						style={{ border: '1px solid #464545', backgroundColor: 'white' }}
					>
						{column.FilterType !== FilterTypes.DateRange ? (
							<div className={'form-group'}>
								{RenderFilterOperations()}
							</div>
						) : (
							RenderFilterOperations()
						)}
					</div>
				</Popup>
				{components.sortElement}
			</>
		)
	}

	const columns = [
		{
			dataField: 'cloudAccountName',
			FilterType: FilterTypes.String,
			text: 'Account Name',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
		},
		{
			dataField: 'cloudTierId',
			FilterType: FilterTypes.Numeric,
			text: 'Plan',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
			formatter: (data, fullData) =>
				cloudTiers.find((cldTr) => cldTr.id === data)?.tierName ??
				(fullData.crmDealId != null ? 'Unknown' : ''),
		},
		{
			dataField: 'documentLimit',
			FilterType: FilterTypes.DynamicOperation,
			text: 'Tier Limit',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
		},
		{
			dataField: 'termPercentage',
			FilterType: FilterTypes.DynamicOperation,
			isDecimal: true,
			text: 'Current Term %',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
			formatter: (data) => (data != null ? `${data.toFixed(2)}%` : ''),
		},
		{
			dataField: 'currentTermDocuments',
			FilterType: FilterTypes.DynamicOperation,
			isDecimal: false,
			text: 'Current Term Volume',
			sort: true,
			headerFormatter: HeaderFormatter,
			headerClasses: 'text-nowrap text-center',
			classes: 'text-center',
		},
		{
			dataField: 'lifeTimeDocuments',
			FilterType: FilterTypes.DynamicOperation,
			isDecimal: false,
			text: 'LifeTime Documents',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
		},
		{
			dataField: 'isPayingCustomer',
			FilterType: FilterTypes.CheckBox,
			allowsEmpty: false,
			text: 'Is Paying Customer',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: (column, colIndex, components) => {
				return HeaderFormatter(column, colIndex, components, [
					'ChargeBee',
					'FastBound',
					'No',
				])
			},
			classes: 'text-center',
		},
		{
			dataField: 'crmDealAmount',
			FilterType: FilterTypes.DynamicOperation,
			text: 'Deal Amount',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
			formatter: CurrencyFormatter,
		},
		{
			dataField: 'crmDealExpectedRevenue',
			FilterType: FilterTypes.DynamicOperation,
			isDecimal: true,
			text: 'Expected Revenue',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
			formatter: (data) => {
				return data != null ? CurrencyFormatter(data / 12) : ''
			},
		},
		{
			dataField: 'chargeBeePlanAmount',
			FilterType: FilterTypes.DynamicOperation,
			isDecimal: true,
			text: 'ChargeBee Amount',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
			formatter: CurrencyFormatter,
		},
		{
			dataField: 'chargeBeeMatchesZoho',
			FilterType: FilterTypes.Boolean,
			allowsEmpty: true,
			text: 'ChargeBee Matches Zoho',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
			formatter: (data) =>
				data !== null ? (data === true ? 'Yes' : 'No') : '',
		},
		{
			dataField: 'termStart',
			FilterType: FilterTypes.DateRange,
			text: 'Term Start',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
			formatter: DateFormatter,
		},
		{
			dataField: 'termStart',
			FilterType: FilterTypes.DateRange,
			text: 'Term End',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
			formatter: (data) => {
				return data != null ? DateFormatter(moment(data).add(1, 'y')) : ''
			},
		},
		{
			dataField: 'cloudAccountEmail',
			FilterType: FilterTypes.String,
			text: 'Account Email',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
		},
		{
			dataField: 'cloudAccountPhone',
			FilterType: FilterTypes.String,
			text: 'Account Phone',
			sort: true,
			headerClasses: 'text-nowrap text-center',
			headerFormatter: HeaderFormatter,
			classes: 'text-center',
		},
	]

	const handleTableChange = (
		type,
		{ page, sizePerPage, sortField, sortOrder }
	) => {
		const FilterAfterPagination = {
			...reportFilters,
			limit: sizePerPage,
			startIndex: sizePerPage * (page - 1),
			page,
			Sort: sortField,
			SortOrder: sortOrder,
		}
		dispatch(setCloudReportFilters(FilterAfterPagination))
		HandleFetchSalesReport(FilterAfterPagination)
	}

	function HandleFiltersToBeSent(localFilters) {
		let FiltersToBeSent
		if (!localFilters) {
			FiltersToBeSent = {
				...reportFilters,
				page: 1,
				startIndex: 0,
				limit: reportFilters.limit ?? 50,
				Sort: reportFilters.Sort,
				SortOrder: reportFilters.SortOrder,
			}
			dispatch(setCloudReportFilters(FiltersToBeSent))
		} else {
			FiltersToBeSent = {
				...localFilters,
			}
		}
		for (const [key, value] of Object.entries(FiltersToBeSent)) {
			if (key === 'termStart' || key === 'termEnd') {
				FiltersToBeSent[`${key}`].StartDate = FiltersToBeSent[`${key}`].start
				FiltersToBeSent[`${key}`].EndDate = FiltersToBeSent[`${key}`].end
				delete FiltersToBeSent[`${key}`].start
				delete FiltersToBeSent[`${key}`].end
			} else if (
				typeof value === 'object' &&
				!Array.isArray(value) &&
				(!FiltersToBeSent[`${key}`]?.filterOperationType == null ||
					(FiltersToBeSent[`${key}`]?.filterOperationType !==
						FilterOperators.IsEmpty &&
						FiltersToBeSent[`${key}`]?.filterOperationType !==
							FilterOperators.NotEmpty &&
						FiltersToBeSent[`${key}`]?.filterValue == null &&
						FiltersToBeSent[`${key}`]?.FilterValueAfter == null &&
						FiltersToBeSent[`${key}`]?.FilterValueBefore == null))
			)
				delete FiltersToBeSent[`${key}`]
			if (key === 'crmDealExpectedRevenue' && value != null)
				for (const [revKey, revValue] of Object.entries(value)) {
					if (revValue != null && revKey !== 'filterOperationType') {
						FiltersToBeSent[`${key}`] = {
							...value,
							[`${revKey}`]: revValue * 12,
						}
					}
				}
		}
		return FiltersToBeSent
	}

	const HandleFetchSalesReport = (localFilters) => {
		const FiltersToBeSent = HandleFiltersToBeSent(localFilters)
		dispatch(
			fetchCloudReport({
				filters: FiltersToBeSent,
				useCaching: false,
			})
		)
	}

	const HandleExportReport = () => {
		setIsExporting(true)
		const filtersToBeSent = HandleFiltersToBeSent()
		dispatch(
			exportCloudReport({ filters: filtersToBeSent, useCaching: false })
		).then(() => {
			setIsExporting(false)
		})
	}

	// Map columns to DataTable ColumnDef
	const mappedColumns = React.useMemo(() => {
		if (!columns || Array.isArray(columns) === false) return []
		return columns.map((col, idx) => {
			const accessorKey = col.dataField || undefined
			const safeId = accessorKey || (typeof col.text === 'string' ? col.text : `col_${idx}`)
			return {
				id: accessorKey ? String(accessorKey) : (String(safeId) || `col_${idx}`),
				header: col.text,
				accessorKey,
				cell: col.formatter
					? ({ getValue, row }) => col.formatter(getValue?.(), row.original)
					: ({ getValue }) => getValue?.(),
				enableSorting: !!(accessorKey && col.sort),
			}
		})
	}, [columns])

	// Derive sorting from filters
	const sorting = React.useMemo(() => {
		if (reportFilters.Sort) {
			return [{ id: reportFilters.Sort, desc: (reportFilters.SortOrder || '').toLowerCase() === 'desc' }]
		}
		return []
	}, [reportFilters.Sort, reportFilters.SortOrder])

	const handleServerPaginationChange = (updater) => {
		const next = typeof updater === 'function' ? updater({ pageIndex: (reportFilters.page || 1) - 1, pageSize: reportFilters.limit }) : updater
		const nextPageIndex = next.pageIndex ?? 0
		const nextPageSize = next.pageSize ?? reportFilters.limit
		dispatch(
			setCloudReportFilters({
				...reportFilters,
				page: nextPageIndex + 1,
				limit: nextPageSize,
				startIndex: nextPageIndex * nextPageSize,
			})
		)
		HandleFetchSalesReport({ ...reportFilters, page: nextPageIndex + 1, limit: nextPageSize, startIndex: nextPageIndex * nextPageSize })
	}

	const handleServerSortingChange = (updater) => {
		const next = typeof updater === 'function' ? updater(sorting) : updater
		const first = Array.isArray(next) && next.length ? next[0] : null
		dispatch(
			setCloudReportFilters({
				...reportFilters,
				Sort: first ? first.id : '',
				SortOrder: first ? (first.desc ? 'desc' : 'asc') : '',
				page: 1,
				startIndex: 0,
			})
		)
		HandleFetchSalesReport({ ...reportFilters, Sort: first ? first.id : '', SortOrder: first ? (first.desc ? 'desc' : 'asc') : '', page: 1, startIndex: 0 })
	}

	return (
		<PageLayout breadcrumbs={[{label: 'Dashboard', href: '/'}, { label: 'Sales report', href: '/sales-report' }]} title='Sales report' action={
		<div className={'mb-2'}>
			<Button className={'mr-2'} onClick={() => HandleFetchSalesReport()}>
				<BusyIndicator region={REGIONS.FETCH_CLOUD_REPORT}>
					Search
				</BusyIndicator>
			</Button>
			<Button
				className={'mr-2'}
				onClick={() => {
					const newFilter = {
						page: 1,
						startIndex: 0,
						limit: reportFilters.limit ?? 50,
						Sort: reportFilters.Sort,
						SortOrder: reportFilters.SortOrder,
					}
					dispatch(setCloudReportFilters(newFilter))
					HandleFetchSalesReport(newFilter)
				}}
			>
				<BusyIndicator region={REGIONS.FETCH_CLOUD_REPORT}>
					Reset Filters
				</BusyIndicator>
			</Button>
			<Button
				disabled={isExporting}
				onClick={() => {
					HandleExportReport()
				}}
			>
				<BusyIndicator region={REGIONS.EXPORT_CLOUD_REPORT}>
					Export
				</BusyIndicator>
			</Button>
		</div>
	}>
			<DataTable
				columns={mappedColumns}
				data={reportsTableResult.cloudReports || []}
				emptyMessage={'No Data Found'}
				enablePagination={true}
				serverPagination={true}
				scrollContainerClassName='max-h-[calc(100vh-400px)] overflow-auto'
				pageIndex={(reportFilters.page || 1) - 1}
				pageSize={reportFilters.limit}
				pageCount={Math.max(1, Math.ceil((reportsTableResult.totalCount || 0) / (reportFilters.limit || 1)))}
				onPaginationChange={handleServerPaginationChange}
				serverSorting={true}
				sorting={sorting}
				onSortingChange={handleServerSortingChange}
			/>
		</PageLayout>
	)
}
export default SalesReport
